import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { AlertCircle, CheckCircle2, LogIn, MapPin, Smartphone, Sparkles, Ticket } from 'lucide-react'
import StampProgress from '../../components/StampProgress'
import StampSuccessAnimation from '../../components/StampSuccessAnimation'
import { auth } from '../../lib/firebase'
import { isWeb3Mode } from '../../lib/authMode'
import { arWebArUrl } from '../../lib/features'
import { gotoWebAr, rememberArEvent } from '../../lib/arLaunch'
import { useGeolocation } from '../../hooks/useGeolocation'
import { useI18n } from '../../i18n/useI18n'
import { api, ApiError, type StampArCompleteResult, type StampArSpot, type StampEvent } from '../../services/api'
import { useStore } from '../../store/useStore'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

type Phase =
  | { kind: 'resolving' }
  | { kind: 'ready'; token: string; event: StampEvent; spot: StampArSpot | null; opened: boolean }
  | { kind: 'opening'; token: string; event: StampEvent; spot: StampArSpot | null }
  | { kind: 'opened'; result: StampArCompleteResult; event: StampEvent }
  | { kind: 'invalid' }
  | { kind: 'closed' }
  | { kind: 'error' }

/**
 * /stamp-rally/ar?spot=<spotId>&v=<qrVersion> — ARスポットの印刷マーカーQRの
 * 着地点 (#1154)。標準カメラで読んだ来場者をここで受け、起動トークンを取って
 * WebAR へ橋渡しする。AR 非対応端末向けフォールバックとして、WebAR を経由せず
 * この画面から直接宝箱を開ける (/stamp/ar/complete) 導線も持つ。
 *
 * auth 解決は StampGate と同型だが、/ar/launch が要ログインなので匿名は
 * ログイン誘導のみ (匿名スキャン相当の集客導線はない)。
 */
export default function ArGate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useI18n()
  const spotId = searchParams.get('spot') ?? ''
  const qrVersionRaw = searchParams.get('v')

  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const restoreSession = useStore((s) => s.restoreSession)
  const login = useStore((s) => s.login)
  const { coords } = useGeolocation() // キャッシュ/デモ偽装のみ (新規プロンプトなし)

  const [web3Mode] = useState(isWeb3Mode)
  const [authResolved, setAuthResolved] = useState(web3Mode)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'resolving' })
  const restoreStartedRef = useRef(false)
  const launchedRef = useRef(false) // StrictMode の二重 launch 防止

  // ---- auth 解決 (StampGate と同型) ----
  useEffect(() => {
    if (web3Mode) return
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user)
      setAuthResolved(true)
    })
    return unsubscribe
  }, [web3Mode])

  useEffect(() => {
    if (!authResolved || isLoggedIn || restoreStartedRef.current) return
    const hasLocalSession = localStorage.getItem('isLoggedIn') === 'true'
    if (hasLocalSession) {
      restoreStartedRef.current = true
      restoreSession().catch(() => {
        restoreStartedRef.current = false
      })
      return
    }
    if (firebaseUser) {
      restoreStartedRef.current = true
      login({
        name: firebaseUser.displayName || 'User',
        email: firebaseUser.email || '',
      }).catch(() => {
        restoreStartedRef.current = false
      })
    }
  }, [authResolved, isLoggedIn, firebaseUser, restoreSession, login])

  const authSettled =
    authResolved &&
    (isLoggedIn || (!firebaseUser && localStorage.getItem('isLoggedIn') !== 'true'))

  // 匿名は launch できないのでログイン誘導 (effect 内 setState を避けるため
  // state ではなくレンダー時の導出にする — react-hooks/set-state-in-effect)
  const needsLogin = authSettled && !isLoggedIn

  // ---- 起動トークン取得 (auth 確定後に1回だけ) ----
  useEffect(() => {
    if (!spotId || !authSettled || !isLoggedIn || launchedRef.current) return
    launchedRef.current = true
    const qrVersion = qrVersionRaw && /^\d+$/.test(qrVersionRaw) ? Number(qrVersionRaw) : undefined
    api
      .launchArSession({ spotId, spotQrVersion: qrVersion })
      .then(async ({ token, event }) => {
        rememberArEvent(event.id)
        // スポット名/開封済みは表示品質のための追加情報 — 失敗しても進める
        let spot: StampArSpot | null = null
        let opened = false
        try {
          const spots = await api.getArSpots(token)
          spot = spots.spots.find((s) => s.id === spotId) ?? null
          opened = spots.openedSpotIds.includes(spotId)
        } catch { /* 表示は generic のまま */ }
        setPhase({ kind: 'ready', token, event, spot, opened })
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setPhase({ kind: 'invalid' })
        else if (e instanceof ApiError && e.status === 410) setPhase({ kind: 'closed' })
        else setPhase({ kind: 'error' })
      })
  }, [spotId, qrVersionRaw, authSettled, isLoggedIn])

  const handleFallbackOpen = async (token: string, event: StampEvent, spot: StampArSpot | null) => {
    setPhase({ kind: 'opening', token, event, spot })
    try {
      const result = await api.completeArSpot(token, spotId, coords ?? undefined)
      setPhase({ kind: 'opened', result, event })
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) setPhase({ kind: 'closed' })
      else if (e instanceof ApiError && e.status === 404) setPhase({ kind: 'invalid' })
      else setPhase({ kind: 'error' })
    }
  }

  if (!spotId) {
    return <GateError title={t('stampRally.arSpotInvalid')} hint={t('stampRally.invalidQrHint')} />
  }

  if (needsLogin) {
    return (
      <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center px-8 text-center safe-top safe-bottom">
        <Sparkles size={48} className="text-gold mb-4" />
        <p className="font-body font-medium text-text-primary mb-1">{t('stampRally.arTitle')}</p>
        <p className="text-xs font-body text-text-tertiary mb-6">{t('stampRally.arLoginRequired')}</p>
        <button
          onClick={() => {
            sessionStorage.setItem(
              POST_LOGIN_REDIRECT_KEY,
              `/stamp-rally/ar?${searchParams.toString()}`,
            )
            navigate('/login')
          }}
          className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white px-8 py-3 text-sm font-body font-medium tap-effect"
        >
          <LogIn size={18} />
          {t('stampRally.loginToStamp')}
        </button>
      </div>
    )
  }

  if (phase.kind === 'resolving') {
    return (
      <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center gap-4 px-6 safe-top safe-bottom">
        <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-yokoso-red animate-spin" />
        <p className="font-body text-sm text-text-secondary">{t('stampRally.gateVerifying')}</p>
      </div>
    )
  }

  if (phase.kind === 'invalid') {
    return <GateError title={t('stampRally.arSpotInvalid')} hint={t('stampRally.invalidQrHint')} />
  }
  if (phase.kind === 'closed') {
    return <GateError title={t('stampRally.eventClosed')} hint={t('stampRally.eventClosedHint')} />
  }
  if (phase.kind === 'error') {
    return <GateError title={t('stampRally.scanError')} hint="" />
  }

  // ---- 開封結果 (フォールバック経由) ----
  if (phase.kind === 'opened') {
    const { result } = phase
    const stamp = result.stamp
    return (
      <div className="min-h-screen bg-warm-white flex flex-col items-center px-6 pt-14 pb-10 safe-top safe-bottom">
        {result.result === 'stamped' && (
          <>
            <StampSuccessAnimation />
            <p className="mt-2 font-display text-xl text-navy">{t('stampRally.arChestGot')}</p>
          </>
        )}
        {result.result === 'already' && (
          <div className="flex flex-col items-center animate-fade-in">
            <CheckCircle2 size={56} className="text-matcha mb-3" />
            <p className="font-body text-text-primary text-center">{t('stampRally.arAlreadyOpened')}</p>
          </div>
        )}
        {result.result === 'geofence_denied' && (
          <div className="flex flex-col items-center animate-fade-in">
            <MapPin size={56} className="text-yokoso-red mb-3" />
            <p className="font-body text-text-primary text-center">{t('stampRally.geofenceDenied')}</p>
          </div>
        )}

        <div className="w-full mt-6">
          <StampProgress
            count={stamp.stampCount}
            goal={stamp.goal}
            justEarned={result.result === 'stamped'}
          />
          {stamp.achieved ? (
            <div className="mt-3 bg-gold/10 rounded-xl px-4 py-3 text-center animate-fade-in">
              <p className="text-sm font-body font-medium text-text-primary mb-2">
                {t('stampRally.achievedBanner')}
              </p>
              <button
                onClick={() =>
                  navigate(`/stamp-rally/ticket?eventId=${encodeURIComponent(phase.event.id)}`)
                }
                className="inline-flex items-center gap-2 rounded-full bg-gold text-white px-6 py-2.5 text-sm font-body font-medium tap-effect"
              >
                <Ticket size={16} />
                {t('stampRally.goToTicket')}
              </button>
            </div>
          ) : (
            result.result === 'stamped' && (
              <p className="mt-3 text-center text-sm font-body text-text-secondary">
                {t('stampRally.oneMore', {
                  remaining: String(Math.max(0, stamp.goal - stamp.stampCount)),
                })}
              </p>
            )
          )}
        </div>
        <BackToTop />
      </div>
    )
  }

  // ---- ready / opening: キャラ発見 → AR で開ける or この端末で開ける ----
  const { token, event, spot } = phase
  const opening = phase.kind === 'opening'
  const alreadyOpened = phase.kind === 'ready' && phase.opened
  // 起動先はイベント設定 (activityUrl) 優先、type='ar' は AR 既定値 (#1159)
  const activityUrl = event.activityUrl || ((event.type ?? 'qr') === 'ar' ? arWebArUrl() : '')

  return (
    <div className="min-h-screen bg-warm-white flex flex-col items-center px-6 pt-14 pb-10 safe-top safe-bottom">
      <Sparkles size={48} className="text-gold mb-3 animate-scale-in" />
      <p className="font-display text-xl text-navy text-center">
        {spot ? t('stampRally.arFoundNamed', { name: spot.name }) : t('stampRally.arFound')}
      </p>
      {spot?.description && (
        <p className="mt-2 text-sm font-body text-text-secondary text-center">
          「{spot.description}」
        </p>
      )}
      <p className="mt-1 text-xs font-body text-text-tertiary">{event.name}</p>

      <div className="w-full mt-8 flex flex-col gap-2">
        {spot?.spotKind === 'creation' ? (
          // みんなのキャラ: 当日に来場者と作って置いたキャラ。スタンプの対象ではないので
          // 開封ボタンは出さず、誰が作ったかを出す
          spot.creatorName ? (
            <p className="text-center text-sm font-body text-text-secondary">
              {t('stampRally.arCreationBy', { name: spot.creatorName })}
            </p>
          ) : null
        ) : spot?.spotKind === 'board' ? (
          // 掲示板: メモを読む・貼るのは AR の中だけ。開封は無いので、AR を開くボタンだけ出す
          // (この端末でそのまま開ける、は宝箱の開封の代わりなので出さない)
          activityUrl ? (
            <>
              <button
                onClick={() => gotoWebAr(token, event.id, spotId, activityUrl)}
                className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect"
              >
                <Sparkles size={18} />
                {t('stampRally.arBoardOpen')}
              </button>
              <p className="text-center text-xs font-body text-text-tertiary">
                {t('stampRally.arBoardHint')}
              </p>
            </>
          ) : null
        ) : spot?.spotKind === 'pr' ? (
          // PR キャラ (#1157 FB): 宣伝文章は上部に表示済み。開封ボタンは出さない
          spot.prLinkUrl ? (
            <a
              href={spot.prLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full bg-navy text-white py-3.5 text-sm font-body font-medium tap-effect"
            >
              {t('stampRally.arPrMore')}
            </a>
          ) : null
        ) : alreadyOpened ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm font-body text-text-secondary">{t('stampRally.arAlreadyOpened')}</p>
          </div>
        ) : (
          <>
            {activityUrl && (
              <button
                onClick={() => gotoWebAr(token, event.id, spotId, activityUrl)}
                disabled={opening}
                className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect disabled:opacity-60"
              >
                <Sparkles size={18} />
                {t('stampRally.arOpenWithAr')}
              </button>
            )}
            <button
              onClick={() => void handleFallbackOpen(token, event, spot)}
              disabled={opening}
              className={`flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-body font-medium tap-effect disabled:opacity-60 ${
                activityUrl ? 'border border-navy text-navy' : 'bg-yokoso-red text-white'
              }`}
            >
              <Smartphone size={18} />
              {opening ? t('stampRally.arOpening') : t('stampRally.arOpenFallback')}
            </button>
            {activityUrl && (
              <p className="text-center text-xs font-body text-text-tertiary">
                {t('stampRally.arOpenFallbackHint')}
              </p>
            )}
          </>
        )}
      </div>
      <BackToTop />
    </div>
  )
}

function BackToTop() {
  const navigate = useNavigate()
  const { t } = useI18n()
  return (
    <button
      onClick={() => navigate('/stamp-rally')}
      className="mt-auto pt-6 text-center text-sm font-body text-text-secondary tap-effect py-2"
    >
      {t('stampRally.backToTop')}
    </button>
  )
}

function GateError({ title, hint }: { title: string; hint: string }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  return (
    <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center px-8 text-center safe-top safe-bottom">
      <AlertCircle size={48} className="text-yokoso-red mb-4" />
      <p className="font-body font-medium text-text-primary mb-1">{title}</p>
      {hint && <p className="text-xs font-body text-text-tertiary mb-6">{hint}</p>}
      <button
        onClick={() => navigate('/stamp-rally')}
        className="rounded-full border border-navy text-navy px-6 py-2.5 text-sm font-body font-medium tap-effect"
      >
        {t('stampRally.backToTop')}
      </button>
    </div>
  )
}
